import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  PencilIcon,
  ClockIcon,
  PlayIcon,
  PauseIcon,
  DocumentDuplicateIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import { formatDate } from '@/utils/formatters';
import toast from 'react-hot-toast';
import {
  getEmailTemplates,
  toggleTemplateActive,
  duplicateTemplate,
} from '@/services/emailTemplateApi';
import type { EmailTemplate, TriggerType } from '@/types/emailTemplate';
import { TRIGGER_TYPE_LABELS, CADENCE_LABELS } from '@/types/emailTemplate';

const STATUS_BADGES = {
  active: { variant: 'success' as const, label: 'Active' },
  inactive: { variant: 'gray' as const, label: 'Inactive' },
};

const TRIGGER_BADGES: Record<TriggerType, { variant: 'info' | 'warning' | 'primary' | 'gray'; label: string }> = {
  user_action: { variant: 'info', label: 'User Action' },
  admin_action: { variant: 'warning', label: 'Admin Action' },
  scheduled: { variant: 'primary', label: 'Scheduled' },
  system: { variant: 'gray', label: 'System' },
};

export default function EmailTemplates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [triggerFilter, setTriggerFilter] = useState<TriggerType | 'all'>('all');
  const [search, setSearch] = useState('');

  // Modals
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [duplicateSlug, setDuplicateSlug] = useState('');
  const [duplicateName, setDuplicateName] = useState('');

  // Fetch templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['emailTemplates', page, statusFilter, triggerFilter, search],
    queryFn: () =>
      getEmailTemplates({
        page,
        limit: 20,
        status: statusFilter,
        trigger_type: triggerFilter,
        search: search || undefined,
      }),
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      toggleTemplateActive(id, isActive),
    onSuccess: (_, { isActive }) => {
      toast.success(isActive ? 'Template activated' : 'Template deactivated');
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
    onError: () => toast.error('Failed to update template'),
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: async ({ id, slug, name }: { id: string; slug: string; name: string }) =>
      duplicateTemplate(id, slug, name),
    onSuccess: (newTemplate) => {
      toast.success('Template duplicated');
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      setShowDuplicateModal(false);
      setSelectedTemplate(null);
      setDuplicateSlug('');
      setDuplicateName('');
      navigate(`/email-templates/${newTemplate.id}`);
    },
    onError: () => toast.error('Failed to duplicate template'),
  });

  // Calculate stats from data
  const stats = useMemo(() => {
    const templates = templatesData?.templates || [];
    return {
      total: templatesData?.total || 0,
      active: templates.filter(t => t.is_active).length,
      inactive: templates.filter(t => !t.is_active).length,
      user_action: templates.filter(t => t.trigger_type === 'user_action').length,
      admin_action: templates.filter(t => t.trigger_type === 'admin_action').length,
      scheduled: templates.filter(t => t.trigger_type === 'scheduled').length,
    };
  }, [templatesData]);

  const columns = useMemo<ColumnDef<EmailTemplate>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Template',
        cell: ({ row }) => (
          <div className="max-w-md">
            <div className="flex items-center gap-2">
              <EnvelopeIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <div>
                <div className="font-medium text-gray-900">{row.original.name}</div>
                <div className="text-sm text-gray-500">{row.original.slug}</div>
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.is_active ? 'active' : 'inactive';
          const badge = STATUS_BADGES[status];
          return (
            <div className="flex items-center gap-2">
              <Badge variant={badge.variant} dot>
                {badge.label}
              </Badge>
              {row.original.is_system && (
                <Badge variant="info">System</Badge>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'trigger_type',
        header: 'Trigger',
        cell: ({ row }) => {
          const trigger = row.original.trigger_type;
          const badge = TRIGGER_BADGES[trigger];
          return (
            <div>
              <Badge variant={badge.variant}>{badge.label}</Badge>
              <div className="text-xs text-gray-500 mt-1 max-w-[200px] truncate" title={row.original.trigger_description}>
                {row.original.trigger_description}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'cadence',
        header: 'Cadence',
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">
            {row.original.cadence ? CADENCE_LABELS[row.original.cadence] || row.original.cadence : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'trigger_function',
        header: 'Function',
        cell: ({ row }) => (
          <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
            {row.original.trigger_function}
          </code>
        ),
      },
      {
        accessorKey: 'version_count',
        header: 'Versions',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <ClockIcon className="w-4 h-4" />
            <span>{row.original.version_count || 1}</span>
          </div>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Last Updated',
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {formatDate(row.original.updated_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                navigate(`/email-templates/${row.original.id}`);
              }}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-gray-100 rounded"
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                toggleActiveMutation.mutate({
                  id: row.original.id,
                  isActive: !row.original.is_active,
                });
              }}
              className={`p-2 hover:bg-gray-100 rounded ${
                row.original.is_active
                  ? 'text-gray-400 hover:text-orange-600'
                  : 'text-gray-400 hover:text-green-600'
              }`}
              title={row.original.is_active ? 'Deactivate' : 'Activate'}
            >
              {row.original.is_active ? (
                <PauseIcon className="w-4 h-4" />
              ) : (
                <PlayIcon className="w-4 h-4" />
              )}
            </button>
            {!row.original.is_system && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setSelectedTemplate(row.original);
                  setDuplicateSlug(`${row.original.slug}-copy`);
                  setDuplicateName(`${row.original.name} (Copy)`);
                  setShowDuplicateModal(true);
                }}
                className="p-2 text-gray-400 hover:text-purple-600 hover:bg-gray-100 rounded"
                title="Duplicate"
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        ),
      },
    ],
    [navigate, toggleActiveMutation]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage email templates used by Resend for transactional emails
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Inactive</p>
            <p className="text-2xl font-bold text-gray-400">{stats.inactive}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">User Triggered</p>
            <p className="text-2xl font-bold text-blue-600">{stats.user_action}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Admin Triggered</p>
            <p className="text-2xl font-bold text-orange-600">{stats.admin_action}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Scheduled</p>
            <p className="text-2xl font-bold text-purple-600">{stats.scheduled}</p>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={triggerFilter}
            onChange={(e) => {
              setTriggerFilter(e.target.value as TriggerType | 'all');
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="all">All Triggers</option>
            <option value="user_action">User Action</option>
            <option value="admin_action">Admin Action</option>
            <option value="scheduled">Scheduled</option>
            <option value="system">System</option>
          </select>
        </div>

        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            id="email-templates-search"
            name="emailTemplatesSearch"
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search templates..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
            aria-label="Search templates"
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={templatesData?.templates || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={20}
            totalCount={templatesData?.total}
            onPageChange={setPage}
            getRowId={(row) => row.id}
            onRowClick={(row) => navigate(`/email-templates/${row.id}`)}
          />
        </CardBody>
      </Card>

      {/* Duplicate Modal */}
      <Modal
        isOpen={showDuplicateModal}
        onClose={() => {
          setShowDuplicateModal(false);
          setSelectedTemplate(null);
          setDuplicateSlug('');
          setDuplicateName('');
        }}
        title="Duplicate Template"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Create a copy of "{selectedTemplate?.name}"
          </p>
          <div>
            <label htmlFor="duplicate-template-name" className="block text-sm font-medium text-gray-700 mb-1">
              New Template Name *
            </label>
            <input
              id="duplicate-template-name"
              name="duplicateName"
              type="text"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="Template name"
              aria-label="New template name"
            />
          </div>
          <div>
            <label htmlFor="duplicate-template-slug" className="block text-sm font-medium text-gray-700 mb-1">
              New Template Slug *
            </label>
            <input
              id="duplicate-template-slug"
              name="duplicateSlug"
              type="text"
              value={duplicateSlug}
              onChange={(e) => setDuplicateSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand font-mono"
              placeholder="template-slug"
              aria-label="New template slug"
            />
            <p className="mt-1 text-xs text-gray-500">
              Used to identify this template in edge functions
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowDuplicateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              selectedTemplate &&
              duplicateMutation.mutate({
                id: selectedTemplate.id,
                slug: duplicateSlug,
                name: duplicateName,
              })
            }
            loading={duplicateMutation.isPending}
            disabled={!duplicateSlug.trim() || !duplicateName.trim()}
          >
            Duplicate
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
